import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, Share2, Gift, Loader2, Check } from "lucide-react";
import api from "../lib/api";
import { isIOS } from "../lib/platform";
import { referralEnabled } from "../lib/featureFlags";
import { APP_REVIEW_LOCKDOWN } from "../lib/appReview";

/**
 * InviteModal — Refer-a-friend dialog.
 *
 * • Loads the user's referral code/link from `GET /api/referral/me`.
 * • Native iOS uses Capacitor `Share` API; web falls back to clipboard.
 * • Shows lifetime invite count + bonus-days copy.
 *
 * T49.I (Build 73) — Re-skinned to match TipLingo V2 design (white cards,
 * navy text, orange accent, Plus Jakarta Sans, rounded-2xl + shadow-soft).
 * Previous V1 "postcard stamp" palette (bone/ink/terracotta/Fraunces) was
 * inconsistent with the rest of the app. Backend `share_url` was also
 * fixed in the same patch to use PUBLIC_WEB_URL instead of the Capacitor
 * Origin header, so the embedded link is now a real `https://tiplingo.com/?ref=...`
 * URL instead of `capacitor://localhost/?ref=...`.
 *
 * Triggered from AccountButton via `openInvite`.
 */
export default function InviteModal({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { code, share_url, invited_count, bonus_days_each }
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/referral/me")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail;
        toast.error(typeof msg === "string" ? msg : "Couldn't load your invite link");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function copyLink() {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success("Invite link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  }

  async function shareLink() {
    if (!data?.share_url) return;
    setSharing(true);
    const shareText = `Join me on TipLingo — we'll both get ${data.bonus_days_each} days of Pro free. ${data.share_url}`;
    try {
      if (isIOS()) {
        // Capacitor Share plugin works in iOS WKWebView.
        const { Share } = await import("@capacitor/share");
        await Share.share({
          title: "Try TipLingo with me",
          text: shareText,
          url: data.share_url,
          dialogTitle: "Invite a friend",
        });
      } else if (navigator.share) {
        await navigator.share({
          title: "Try TipLingo with me",
          text: shareText,
          url: data.share_url,
        });
      } else {
        await copyLink();
      }
    } catch (e) {
      // User cancelled — silent. Real errors fall through to clipboard.
      if (e?.message && !/cancel/i.test(e.message)) {
        await copyLink();
      }
    } finally {
      setSharing(false);
    }
  }

  if (!open) return null;
  // T49.R (Build 79) — Hardcoded App Review lockdown short-circuit.
  // `APP_REVIEW_LOCKDOWN` is `true` literal in /lib/appReview.js for
  // this build, so webpack DCE collapses this into `return null`
  // unconditionally. Defends against any code path that might call
  // setInviteOpen(true) without going through the gated trigger.
  if (APP_REVIEW_LOCKDOWN) return null;
  // T49.J (Build 74) — defensive guard. Even if a parent forgets to
  // gate the trigger, the modal itself refuses to mount when the
  // referral feature flag is off. This is the App-Review safety net.
  if (!referralEnabled()) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm animate-fade-up"
      onClick={onClose}
      data-testid="invite-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-soft max-w-sm w-full p-6 relative"
        data-testid="invite-modal"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="invite-modal-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-navy/50 hover:text-navy hover:bg-navy/5 transition"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-full bg-orange/15 text-orange flex items-center justify-center">
            <Gift size={20} strokeWidth={2.5} />
          </div>
          <div className="font-jakarta font-extrabold text-2xl text-navy leading-none">
            Give 7, get 7
          </div>
        </div>

        <p className="font-jakarta text-sm text-navy/70 leading-relaxed mb-5">
          Invite a travel buddy. When they sign up with your link, you{" "}
          <span className="font-bold text-navy">both get 7 days of Pro free</span>
          {" "}— expanded Pro access for AI translations, voice + menu scans,
          and Pro tipping tools.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-navy/40">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : data ? (
          <>
            <div className="mb-4">
              <label className="block text-[10px] font-jakarta font-bold uppercase tracking-widest text-navy/50 mb-1.5">
                Your invite code
              </label>
              <div
                className="font-mono text-xl tracking-[0.3em] text-center text-navy bg-cream rounded-2xl py-3.5 border border-navy/5"
                data-testid="invite-code"
              >
                {data.code}
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-[10px] font-jakarta font-bold uppercase tracking-widest text-navy/50 mb-1.5">
                Share link
              </label>
              <div className="flex items-stretch gap-2">
                <input
                  readOnly
                  value={data.share_url}
                  onClick={(e) => e.target.select()}
                  data-testid="invite-share-url"
                  className="flex-1 min-w-0 bg-cream rounded-2xl px-3 py-2.5 font-mono text-[11px] text-navy border border-navy/5 focus:outline-none focus:ring-2 focus:ring-orange/30"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="invite-copy-btn"
                  className="bg-white border border-navy/10 rounded-2xl px-3.5 hover:bg-cream active:scale-[0.97] transition"
                  aria-label="Copy link"
                >
                  {copied ? (
                    <Check size={16} strokeWidth={2.5} className="text-orange" />
                  ) : (
                    <Copy size={16} strokeWidth={2.5} className="text-navy/70" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={shareLink}
              disabled={sharing}
              data-testid="invite-share-btn"
              className="w-full flex items-center justify-center gap-2 bg-orange text-white font-jakarta font-bold text-sm py-3.5 rounded-full shadow-soft hover:bg-orange-soft hover:text-orange active:scale-[0.98] disabled:opacity-60 transition"
            >
              {sharing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Share2 size={16} strokeWidth={2.5} />
              )}
              Share invite
            </button>

            <div className="mt-5 pt-4 border-t border-navy/5 text-center">
              <div className="font-jakarta font-bold text-[10px] uppercase tracking-widest text-navy/50">
                Friends invited
              </div>
              <div
                className="font-jakarta font-extrabold text-3xl text-navy mt-0.5"
                data-testid="invite-count"
              >
                {data.invited_count}
              </div>
              {data.invited_count > 0 && (
                <div className="font-jakarta font-semibold text-xs text-orange mt-1">
                  +{data.invited_count * data.bonus_days_each} bonus days earned
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
